import { useState } from "react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Combobox as SimpleCombobox } from "@headlessui/react";
import { useController } from "react-hook-form";
import { getNestedValue } from "../../helpers";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Combobox({
  options,
  label,
  control,
  name,
  inputSize = "xl",
  disabled,
}) {
  const [query, setQuery] = useState("");

  const {
    field,
    formState: { errors },
  } = useController({ name, control });

  const filteredOption =
    query === ""
      ? options
      : options.filter((o) =>
          o.name.toLowerCase().includes(query.toLowerCase())
        );

  const errorForField = getNestedValue(errors, name);

  const sizeClasses = {
    sm: "py-1 text-sm",
    md: "py-1.5 text-sm",
    lg: "py-2 text-base",
    xl: "py-3 text-base",
  };

  return (
    <>
      <SimpleCombobox
        as="div"
        value={field.value}
        onChange={field.onChange}
        {...field}
        disabled={disabled}
      >
        <SimpleCombobox.Label className="block text-sm font-light leading-6 text-gray-900">
          {label}
        </SimpleCombobox.Label>

        <div className="relative mt-2">
          <SimpleCombobox.Input
            data-testid={name}
            className={classNames(
              errorForField
                ? "ring-red-300 text-red-900 focus:ring-red-600"
                : "ring-gray-200 text-gray-900 focus:ring-blue",
              "w-full rounded-md border-0 bg-white pl-3 pr-10 ring-1 ring-inset focus:outline-none focus:ring-2 focus:ring-inset sm:leading-6",
              sizeClasses[inputSize] || sizeClasses.md
            )}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(option) => option?.name}
          />

          <SimpleCombobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </SimpleCombobox.Button>

          {filteredOption.length > 0 && (
            <SimpleCombobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredOption.map((option) => (
                <SimpleCombobox.Option
                  key={option.id}
                  value={option}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-3 pr-9",
                      active ? "bg-blue text-white" : "text-gray-900"
                    )
                  }
                >
                  {({ active, selected }) => (
                    <>
                      <span
                        className={classNames(
                          "block truncate",
                          selected && "font-semibold"
                        )}
                      >
                        {option.name}
                      </span>

                      {selected && (
                        <span
                          className={classNames(
                            "absolute inset-y-0 right-0 flex items-center pr-4",
                            active ? "text-white" : "text-blue"
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </SimpleCombobox.Option>
              ))}
            </SimpleCombobox.Options>
          )}
        </div>
      </SimpleCombobox>

      {errorForField && (
        <p className="mt-2 text-sm text-red-600" id={`${name}-error`}>
          {errorForField.message}
        </p>
      )}
    </>
  );
}
